import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { LoginResponseDto, MeDto, StoreAccessDto } from '@weloze/shared';
import { api, ApiError } from './api';

interface AuthState {
  user: MeDto | null;
  /** `true` enquanto a sessão existente ainda não foi verificada no servidor. */
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  /** Loja selecionada no seletor do topo. */
  currentStore: StoreAccessDto | null;
  setCurrentStore: (store: StoreAccessDto) => void;
}

const AuthContext = createContext<AuthState | null>(null);

const STORE_KEY = 'weloze.currentStoreId';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<MeDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentStoreId, setCurrentStoreId] = useState<string | null>(() =>
    localStorage.getItem(STORE_KEY),
  );

  /**
   * Ao abrir o app não sabemos se há sessão: o cookie é httpOnly e o JavaScript
   * não consegue lê-lo. A única forma de saber é perguntar ao servidor.
   */
  const refresh = useCallback(async () => {
    try {
      setUser(await api.get<MeDto>('/auth/me'));
    } catch (error) {
      // Qualquer falha aqui significa "siga como não autenticado": sem sessão,
      // sessão expirada ou servidor fora. Propagar quebraria o app inteiro na
      // primeira renderização, e a tela de login é justamente o destino certo
      // nos três casos. Erro de infraestrutura fica no console para diagnóstico.
      if (!(error instanceof ApiError && error.isUnauthenticated)) {
        console.error('Falha ao verificar a sessão:', error);
      }
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const login = useCallback(async (email: string, password: string) => {
    const { user: logged } = await api.post<LoginResponseDto>('/auth/login', { email, password });
    setUser(logged);
    setLoading(false);
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } finally {
      setUser(null);
      setCurrentStoreId(null);
      localStorage.removeItem(STORE_KEY);
    }
  }, []);

  const setCurrentStore = useCallback((store: StoreAccessDto) => {
    setCurrentStoreId(store.storeId);
    localStorage.setItem(STORE_KEY, store.storeId);
  }, []);

  const currentStore = useMemo(() => {
    if (!user || user.stores.length === 0) return null;
    // A loja guardada pode ter perdido o acesso desde a última visita: nesse
    // caso cai na primeira disponível, em vez de ficar num estado inválido.
    return user.stores.find((s) => s.storeId === currentStoreId) ?? user.stores[0] ?? null;
  }, [user, currentStoreId]);

  const value = useMemo<AuthState>(
    () => ({ user, loading, login, logout, refresh, currentStore, setCurrentStore }),
    [user, loading, login, logout, refresh, currentStore, setCurrentStore],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth precisa estar dentro de <AuthProvider>.');
  return context;
}
